// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { navigationDrawerStyles } from "@sc/Overrides/Components/NavigationDrawer";
import { constants } from "@sc/XDL/constants";
import NavigationArrowRight from "@sc/XDL/Icons/NavigationArrowRight";
import { buttonTransparent } from "@sc/XDL/PreStyle/button";
import { color, styledScrollbar, xScreen } from "@sc/XDL/Utilities";
import { css } from "styled-components";

const selected = css`
    background: ${color("common.ui12")};
    border-radius: 4px;
    & ${IconWrapper} {
        color: ${color("common.ui9")};
    }
`;

navigationDrawerStyles.menuTriggerButton = {
    size: 18,
    variant: "tertiary",
    css: css`
        &,
        &:not(:disabled):hover,
        &:not(:disabled):focus {
            padding: 6px;
            border: none;
            background-color: transparent;
        }
        ${IconWrapper} {
            color: ${color("text.link")};
            &,
            svg {
                height: 16px;
            }
        }
    `,
};
navigationDrawerStyles.drawerSectionSeparator = {
    css: css`
        margin: 24px auto;
        border-top: 1px solid ${color("common.border")};
        width: 100%;
    `,
};
navigationDrawerStyles.headerLinksSeparator = {
    css: css`
        margin: 18px auto;
        border-top: 1px solid ${color("common.border")};
        width: 100%;
    `,
};

navigationDrawerStyles.drawer = {
    mergeCss: true,
    cssOverrides: {
        drawerBody: css`
            transform: translate(-100%, 0);
            height: calc(100% - ${constants.header.tablet.size.authenticated + 1}px);
            top: ${constants.header.tablet.size.authenticated + 1}px;
            box-shadow: none;
            padding: 10px 8px;
            background-color: ${color("common.white")};
            overflow: hidden;
            ${xScreen("lte", 767)} {
                height: calc(100% - ${constants.header.mobile.size.authenticated + 1}px);
                top: ${constants.header.mobile.size.authenticated + 1}px;
                width: 100%;
            }
        `,
        drawerTitle: css`
            display: none;
        `,
        scrim: css`
            background-color: ${color("common.black", 0.33)};
            height: calc(100% - ${constants.header.tablet.size.authenticated + 1}px);
            top: ${constants.header.tablet.size.authenticated + 1}px;
            ${xScreen("lte", 767)} {
                height: calc(100% - ${constants.header.mobile.size.authenticated + 1}px);
                top: ${constants.header.mobile.size.authenticated + 1}px;
            }
        `,
    },
};

navigationDrawerStyles.unAuthenticatedDrawer = {
    mergeCss: true,
    cssOverrides: {
        drawerBody: css`
            transform: translate(-100%, 0);
            height: calc(100% - ${constants.header.tablet.size.unAuthenticated + 1}px);
            top: ${constants.header.tablet.size.unAuthenticated + 1}px;
            box-shadow: none;
            padding: 10px 8px;
            background-color: ${color("common.white")};
            overflow: hidden;
            ${xScreen("lte", 767)} {
                height: calc(100% - ${constants.header.tablet.size.unAuthenticated + 1}px);
                top: ${constants.header.mobile.size.unAuthenticated + 1}px;
                width: 100%;
            }
        `,
        drawerTitle: css`
            display: none;
        `,
        scrim: css`
            background-color: ${color("common.black", 0.33)};
            height: calc(100% - ${constants.header.tablet.size.unAuthenticated + 1}px);
            top: ${constants.header.tablet.size.unAuthenticated + 1}px;
            ${xScreen("lte", 767)} {
                height: calc(100% - ${constants.header.tablet.size.unAuthenticated + 1}px);
                top: ${constants.header.mobile.size.unAuthenticated + 1}px;
            }
        `,
    },
};

navigationDrawerStyles.drawerSectionWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        row-gap: 4px;
        ${styledScrollbar()}
    `,
};
navigationDrawerStyles.sectionWrapper = {
    css: css`
        padding: 0 8px;
        .contact-number {
            padding-left: 0;
            margin-top: 10px;
        }
    `,
};

navigationDrawerStyles.mainNavigationRow = {
    css: css`
        margin: 0;
        padding: 6px 8px;
        border-radius: 4px;
        min-height: 34px;
        width: 100%;
        ${IconWrapper} {
            color: ${color("text.link")};
            height: 22px;
            width: 27px;
            padding: 10px;
        }

        &:focus,
        &:hover {
            ${selected}
        }
    `,
    moreIconProps: {
        src: NavigationArrowRight,
    },
    isCurrentCss: selected,
};

navigationDrawerStyles.headerMainNavigationRow = {
    css: css`
        margin: 0;
        padding: 6px 0;
        border-radius: 4px;
        min-height: 34px;
        width: 100%;
        ${IconWrapper} {
            color: ${color("text.link")};
            height: 22px;
            width: 7px;
        }

        &:focus,
        &:hover {
            ${selected}
        }
    `,
    moreIconProps: {
        src: NavigationArrowRight,
    },
    isCurrentCss: selected,
};
navigationDrawerStyles.signOutRow = {
    css: css`
        padding: 0;
        border-radius: 4px;
        min-height: unset;
        width: 100%;

        &:focus,
        &:hover {
            ${selected}
        }
    `,
};

navigationDrawerStyles.signOutRowText = {
    transform: "capitalize",
    color: "common.black",
    weight: "bold",
    size: 14,
    lineHeight: "22px",
};

navigationDrawerStyles.mainNavigationRowTypography = {
    transform: "capitalize",
    color: "common.black",
    weight: "bold",
    size: 14,
    lineHeight: "22px",
};
navigationDrawerStyles.panelMenu = {
    cssOverrides: {
        wrapper: css`
            > a,
            button {
                height: auto;
                min-height: unset;
            }
        `,
    },
    panelChildrenSeparator: {
        css: css`
            height: 1px;
            border-top: 1px solid ${color("common.border")};
            margin: 0 auto 8px auto;
            width: calc(100% - 32px);
        `,
    },
    backButton: {
        css: css`
            min-width: 0;
            display: flex;
            justify-content: flex-start;
            flex: 1;
            padding: 0;
            align-items: center;

            ${TypographyStyle} {
                color: ${color("text.link")};
            }
            ${IconWrapper} {
                margin-right: 8px;
                color: ${color("text.link")};
                top: 0;
            }
            ${buttonTransparent}
        `,
    },
};
navigationDrawerStyles.menuRowIcon = {
    size: 14,
    css: css`
        margin-right: 8px;
        display: inline-block;
        text-align: center;
        vertical-align: middle;
        &&&&,
        &&& svg {
            color: ${color("text.link")};
            height: 14px;
            width: 14px;
        }
    `,
};
navigationDrawerStyles.panelSectionWrapper = {
    css: css`
        height: 22px;
        display: flex;
        color: ${color("primary.main")};
    `,
};
navigationDrawerStyles.logoLinks = {
    typographyProps: {
        ellipsis: true,
        lineHeight: "22px",
        size: 14,
    },
};
navigationDrawerStyles.currencySymbol = {
    size: 14,
    css: css`
        margin-right: 8px;
        &&&&,
        &&& svg {
            color: ${color("text.link")};
            height: 14px;
            width: 14px;
        }
    `,
};
navigationDrawerStyles.languageCurrencyWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-top: 4px;
    `,
};
